import * as graphics from "@client/app/graphics";
import {DateCarousel} from "@client/components/DateCarousel";
import * as dates from "@client/dates";
import * as boot from "@client/features/boot";
import * as healthkit from "@client/features/healthkit";
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {type AppDispatch, useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import * as Sentry from "@sentry/browser";
import * as React from "react";

async function finishBootStrapAndCheckForUpdates(dispatch: AppDispatch) {
    const init = await dispatch(boot.wrappedBootstrap());

    if (init == false) {
        return false;
    }

    dispatch({type: "SET_NEXT_ANIMATION", animation: "fade"});

    const backgroundNotification = await hardware.getBackgroundNotification();

    if (backgroundNotification != null) {
        void analytics.track("WOKEN_FROM_BACKGROUND", {}, {send_immediately: true});
    }

    if (init.redirectUrl != null) {
        localStorage.setItem("redirectUrl", init.redirectUrl);
    }

    const didUpdate = await dispatch(
        boot.downloadUpdate(
            init.update,
            backgroundNotification == null ? "launch" : "background",
        ),
    );

    if (didUpdate == true) {
        return true;
    }
    const delayedRedirectUrl = localStorage.getItem("redirectUrl");

    if (delayedRedirectUrl != null) {
        localStorage.removeItem("redirectUrl");
    }

    void hardware.nativeOnly(() => hardware.HotPlugin.finishedLaunching());

    return {
        ...init,
        redirectUrl: init.redirectUrl ?? delayedRedirectUrl,
    };
}

function Legacy() {
    const dispatch = useAppDispatch();
    const today = dates.formatISO(new Date(), {representation: "date"});
    const [hasError, setHasError] = React.useState(false);
    const profile = useAppSelector(orm.getProfile);

    React.useEffect(() => {
        void finishBootStrapAndCheckForUpdates(dispatch).then((init) => {
            if (init == false) {
                setHasError(true);
                return;
            }
            if (init == true) {
                return;
            }

            if (init.redirectUrl != null) {
                router.unsafeTransitionByUrl(init.redirectUrl, "replace");
                return;
            }
            if (init.data != null) {
                void healthkit.checkAndSyncHealthData({
                    profile: init.data.profile,
                    trace: "boot",
                });
                void router.replace(
                    "journal.journal",
                    {key: init.data.journal.day.key},
                    {journal: init.data.journal},
                );
            } else if (profile != null) {
                // This is in case we came from /register/ and init had already
                // finished with no data.
                void router.replace("journal.journal", {key: today});
            } else {
                // router.replace("share.index", {share_uuid: "xm4dYZb3DANbZjja3jXnHa"});

                const runOnlyFirstLaunchAfterUpdates = localStorage.getItem(
                    "runOnlyFirstLaunchAfterUpdates",
                );

                if (runOnlyFirstLaunchAfterUpdates != null) {
                    localStorage.removeItem("runOnlyFirstLaunchAfterUpdates");
                    Sentry.setTag("replay_id", "APP_INSTALL");
                    const replay = Sentry.getReplay();
                    if (replay != null) {
                        void analytics.track("STARTED_REPLAY", {
                            replay_id: "APP_INSTALL",
                        });
                        replay.start();
                    } else {
                        void analytics.track("REPLAY_ERROR");
                    }
                }

                void router.replace("register.index", {});
            }
        });
    }, []);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
            }}
        >
            {hasError == false ? (
                <div style={{top: -50, width: 100, height: 100, position: "relative"}}>
                    {/* Align the loading indicator to the center of the screen */}
                    <graphics.Icon
                        style={{
                            position: "absolute",
                            height: "80%",
                            width: "80%",
                            inset: "10%",
                            zIndex: 5,
                        }}
                        title="Joy logo"
                    />
                    <graphics.Loading
                        style={{position: "relative", zIndex: 6}}
                        title="Loading indicator"
                    />
                </div>
            ) : (
                <div
                    style={{
                        textAlign: "center",
                        display: "grid",
                        gap: 15,
                        justifyItems: "center",
                    }}
                >
                    <graphics.ErrorBooting title="Error" />
                    <typography.Title>Connection Error</typography.Title>
                    <typography.P>
                        Joy requires an active internet connection to use.
                    </typography.P>
                    <typography.P>
                        Make sure you are connected and restart the app.
                    </typography.P>
                </div>
            )}
        </div>
    );
}

export const Home = router.Base.useConditions(() => {
    // So we can have the conditional useHooks and not error out
    // when postLogin switches the experience.
    const experience = React.useMemo(
        () => localStorage.getItem("experience") ?? null,
        [],
    ) as "legacy" | "method" | null;

    /*
    Currently unused. Theoretically, this could redirect right to the login.
    But we need to set local storage, and we need to wait for the update check
    to finish still. Maybe sync.  I think a central "boot" that some components
    can await, some not, would be ideal.

    React.useEffect(() => {
        if (experience == null) {
            void finishBootStrapAndCheckForUpdates(dispatch).then((init) => {
                if (init == false) {
                    return;
                }
                if (init == true) {
                    return;
                }

                router.push("login.index", {});
            });
        }
    }, []);
    */

    if ([].length == 1 && experience == null) {
        return {
            view: null,
            options: {
                title: "",
                scene: {kind: "BLANK"},
            },
            actions: [],
        };
    }

    const date = dates.formatISO(new Date(), {representation: "date"});
    const title = utils.formatDate(date);

    return {
        view: <Legacy />,
        options: {
            scene: {kind: "NO_BOTTOM_BORDER"},
            title,
            titleSection: (
                <DateCarousel
                    onPreviousClick={() => {}}
                    previousDayLink={null}
                    onDateClick={() => {}}
                    nextDayLink={null}
                    date={date}
                />
            ),
        },
        actions: [],
    };
});
